// The single client-side analytics surface. Every component imports `track`
// from here rather than touching posthog-js directly, so event names and the
// upsell `Trigger` taxonomy live in one place and can't drift.
//
// Init happens once in `src/instrumentation-client.ts` (Next 16's pre-hydration
// analytics hook) — see initPostHog() below. Everything no-ops when
// NEXT_PUBLIC_POSTHOG_KEY is unset, so dev / test / CI stay silent with zero
// config and no network calls.
//
// Ingestion is reverse-proxied through `/ingest` (see next.config.ts rewrites)
// so ad-blockers don't drop events and we use first-party cookies. The proxy
// targets PostHog US Cloud.

import posthog from 'posthog-js';
import analyticsEvents from '../../../contracts/analytics-events.json';

// The spine of the monetization analysis: one source value threaded through
// every upsell event (feature_gate_viewed → upgrade_modal_opened →
// checkout_started → purchase_completed) so each upsell point is a separable
// funnel. Phase 2+ wires these through; defined here so the taxonomy is fixed.
export type Trigger =
  | 'furniture'
  | 'custom_panels'
  | 'audio_preview'
  | 'recompute'
  // The persistent Upgrade control in the app-shell headers (UpgradeButton).
  // It is on every header, anonymous marketing traffic included.
  //
  // 'room_unlock', 'header_badge' and 'room_card' used to sit alongside it, for
  // upsells that opened from the per-room premium unlock. That whole surface is
  // gone — credits buy simulations now, not rooms — so nothing emits them. Old
  // PostHog events carrying those values are unaffected; this union only types
  // what the app can still send.
  | 'header_upgrade'
  // The modal reopening itself after it sent a logged-out buyer through
  // /login. Its conversion rate is the cost of that detour.
  | 'post_login'
  | 'in_editor_prompt'
  | 'scan_exhausted'
  | 'eval_remaining'
  | 'room_limit'
  | 'design_limit'
  // The automatic plans screen shown once per browser session (any route —
  // landing, editor, a shared room) to a visitor who isn't already
  // unlimited. Not a limit hit, so it stays out of LIMIT_TRIGGERS below.
  //
  // Retired 2026-09-09 with SessionPricingGate.tsx as "measured dead", then
  // RESTORED 2026-09-12 when purchase attribution said otherwise: the last
  // three purchases before the deletion (3-4 Sep, all semiannual) came
  // through this trigger -- three of the last five in the tracked history.
  // "Measured dead" had counted the surface's own click-through and missed
  // what it closed.
  | 'session_start'
  // R2's post-onboarding pre-upsell -> plans sequence (afterGenerate in
  // onboarding/onboardingSteps.tsx), restored 2026-09-09 after
  // `a8784838`/`81502147` had disabled it. Dismissible (a subscriber
  // graduates straight through, and the drawn/pre-made variants both offer a
  // free exit), so it stays out of LIMIT_TRIGGERS below, same as
  // 'session_start'.
  | 'post_onboarding'
  // Editor locks (2026-09-09): Optimize / Place treatment / the Panels rail
  // tab stay visible for a free user and open this modal instead of running.
  // All three are real feature gates -- see LIMIT_TRIGGERS below.
  | 'optimize_locked'
  | 'treatment_locked'
  | 'panels_tab_locked'
  // The checklist sheet's free-tier content — "Unlock checklist"
  // (docs/plans/2026-09-14-av-gear-ui-brief.md §4 "Checklist"/"Free
  // content"). A real feature gate (Pro-only content behind a count), so it
  // joins LIMIT_TRIGGERS below alongside the other *_locked triggers.
  | 'gear_checklist'
  // The Gear rail's "Fits my room" chip on the Receivers-and-processors and
  // Power-amps category pages (docs/plans/2026-09-16-gear-requirements-
  // brief.md §5) — a Free tap opens the modal instead of filtering. Same
  // shape as 'gear_checklist': a real feature gate, joins LIMIT_TRIGGERS.
  | 'gear_fits_filter'
  // The free Room Acoustics Simulator's daily cap (3 runs a day on a free
  // account, plan S23): a real limit hit, joins LIMIT_TRIGGERS.
  | 'simulator_daily_limit';

/** Snake_case PostHog event names — loaded from contracts/analytics-events.json. */
export const EVENTS = analyticsEvents;

const LIMIT_TRIGGERS: readonly Trigger[] = [
  'simulator_daily_limit',
  'scan_exhausted',
  'eval_remaining',
  'room_limit',
  'design_limit',
  'audio_preview',
  'furniture',
  'custom_panels',
  'recompute',
  'optimize_locked',
  'treatment_locked',
  'panels_tab_locked',
  'gear_checklist',
  'gear_fits_filter',
];

/** True when an upgrade modal open is a real feature gate (a limit trigger),
 *  not a browse-the-plans surface like header_upgrade. */
export function isFeatureGate(trigger: Trigger | undefined): boolean {
  if (!trigger) return false;
  return LIMIT_TRIGGERS.includes(trigger);
}

let started = false;

/** Initialise PostHog exactly once, before the app hydrates. No-op without a
 *  key (dev/test/CI) and no-op on the server. Called from instrumentation-client. */
export function initPostHog(): void {
  if (started || typeof window === 'undefined') return;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    // Same-origin proxy (next.config.ts rewrites /ingest → us.i.posthog.com).
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    // Modern defaults: SPA-aware $pageview on history changes + $pageleave
    // (free scroll-depth + time-on-page) + sensible autocapture. The explicit
    // flags below pin the behaviours this product depends on.
    defaults: '2025-05-24',
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,
    // Anonymous events are still captured (the landing → signup funnel needs
    // them) but no person profile is created until identify() — keeps cost and
    // privacy surface down. The anonymous journey merges into the user on
    // identify (see AnalyticsIdentity).
    person_profiles: 'identified_only',
    // Session replay: mask every input (email/password never recorded) and any
    // element tagged data-ph-mask. Actual recording is also gated by the
    // project-level "Record user sessions" toggle in PostHog.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') ph.debug();
    },
  });
  started = true;
}

/** Capture a product event. No-op until init has run with a key. */
export function track(event: string, props?: Record<string, unknown>): void {
  if (!started) return;
  posthog.capture(event, props);
}

/** Fire an event at most once per browser tab session (sessionStorage). */
export function trackOnce(event: string, dedupeKey: string, props?: Record<string, unknown>): void {
  if (!started || typeof window === 'undefined') return;
  const storageKey = `ph_once:${event}:${dedupeKey}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Private mode / disabled storage — still emit once per load via started flag only.
  }
  track(event, props);
}

/** Attach properties to EVERY subsequent event from this browser session.
 *
 *  Used for the acquisition angle (`campaign_angle`, see lib/campaign/
 *  resolveAngle.ts): registering it once makes the entire funnel splittable
 *  by which ad the visitor clicked -- landing, each onboarding step, the
 *  solve, the paywall and the purchase -- without editing a single existing
 *  `track()` call, and without a per-event property that would drift the
 *  moment someone adds a new event and forgets it.
 *
 *  Persisted by posthog-js across reloads within the session, and cleared by
 *  `resetUser` on logout. Keep the payload small and non-identifying: every
 *  event pays for it on the wire, and super properties are the easiest place
 *  to leak PII into analytics by accident. */
export function registerSuperProperties(props: Record<string, unknown>): void {
  if (!started) return;
  posthog.register(props);
}

/** PostHog exception capture — no-op until init has run with a key. */
export function captureException(error: unknown, props?: Record<string, unknown>): void {
  if (!started) return;
  posthog.captureException(error, props);
}

/** Trim client error text for analytics — no stack traces or PII. */
export function safeErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message.slice(0, 200);
  if (typeof error === 'string') return error.slice(0, 200);
  return 'unknown error';
}

/** Tie the current (anonymous) browser identity to a user and set/refresh
 *  person properties. Merges the pre-signup anonymous journey into the user. */
export function identifyUser(distinctId: string, props?: Record<string, unknown>): void {
  if (!started) return;
  posthog.identify(distinctId, props);
}

/** Clear identity on logout so the next user on this browser starts clean.
 *  Guarded on identified state: calling reset() on an anonymous visitor would
 *  mint a fresh anonymous id on every page load and break the landing → signup
 *  funnel's cross-load continuity. posthog persists the identified flag, so the
 *  guard is correct across full reloads (e.g. logout that hard-navigates). */
export function resetUser(): void {
  if (!started) return;
  const ph = posthog as unknown as { _isIdentified?: () => boolean };
  if (ph._isIdentified?.()) posthog.reset();
}
